import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";

const pageInfoFrom = (data: any) => ({
  pageNumber: data?.pageNumber || 1,
  pageSize: data?.pageSize || 10,
  totalItems: data?.totalRecords || 0,
  totalPages: Math.ceil((data?.totalRecords || 0) / (data?.pageSize || 10)),
});

type BillPaymentFilters = {
  token: string;
  searchParam?: string;
  email?: string;
  status?: string | number;
  source?: string | number;
  startDate?: string;
  endDate?: string;
  pageNumber?: number;
};

const initialState = {
  payments: null as any[] | null,
  selectedPayment: null as any,
  summary: null as any,
  loading: false,
  error: undefined as string | undefined,
  pageInfo: {pageNumber: 1, pageSize: 10, totalItems: 0, totalPages: 0},
};

export const getBillPayments = createAsyncThunk<any, BillPaymentFilters>(
  "billPayments/list",
  async (data) =>
    adminRequest(`/bill-payments${buildQuery({
      searchParam: data.searchParam,
      email: data.email,
      status: data.status,
      source: data.source,
      startDate: data.startDate,
      endDate: data.endDate,
      pageNumber: data.pageNumber || 1,
      pageSize: 10,
    })}`, {token: data.token})
);

export const getBillPaymentById = createAsyncThunk<any, {token: string; transactionId: string}>(
  "billPayments/detail",
  async (data) => adminRequest(`/bill-payments/${data.transactionId}`, {token: data.token})
);

export const refundFailedBillPayment = createAsyncThunk<any, {token: string; transactionId: string; reason?: string}>(
  "billPayments/refundFailed",
  async (data) => adminRequest(`/bill-payments/${data.transactionId}/refund`, {
    method: "POST",
    token: data.token,
    body: JSON.stringify({reason: data.reason}),
  })
);

export const retryFailedBillPayment = createAsyncThunk<any, {token: string; transactionId: string}>(
  "billPayments/retryFailed",
  async (data) => adminRequest(`/bill-payments/${data.transactionId}/retry`, {
    method: "POST",
    token: data.token,
  })
);

const billPaymentSlice = createSlice({
  name: "billPayments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBillPayments.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(getBillPayments.fulfilled, (state, action) => {
        const report = getReturnedObject(action.payload);
        state.loading = false;
        state.summary = report;
        state.payments = (report?.payments || []).map((item: any) => ({...item, key: item.id}));
        state.pageInfo = pageInfoFrom(report);
      })
      .addCase(getBillPaymentById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
        state.selectedPayment = null;
      })
      .addCase(getBillPaymentById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPayment = getReturnedObject(action.payload);
      })
      .addCase(refundFailedBillPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPayment = getReturnedObject(action.payload);
      })
      .addCase(retryFailedBillPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPayment = getReturnedObject(action.payload);
      })
      .addCase(getBillPayments.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.error?.message;
      })
      .addCase(getBillPaymentById.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.error?.message;
      });
  },
});

export default billPaymentSlice.reducer;
